import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hash, Type, Trophy, Clock, Target, TrendingUp, User, Flame, Calendar, Grid3x3 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import Header from "../components/Header";
import TrialBanner from "../components/TrialBanner";
import ReferralCard from "../components/ReferralCard";
import AchievementsCard from "../components/AchievementsCard";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { useSubscription } from "../lib/subscription";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("week");
  const { sub } = useSubscription();

  useEffect(() => {
    fetchStats();
    fetchStreak();
  }, [timeframe]);

  const fetchStats = async () => {
    try {
      const response = await authFetch(`${API}/stats?timeframe=${timeframe}`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const fetchStreak = async () => {
    try {
      const response = await authFetch(`${API}/streak`);
      if (response.ok) {
        const data = await response.json();
        setStreak(data);
      }
    } catch (error) {
      console.error("Failed to load streak");
    }
  };

  const formatChartData = () => {
    if (!stats?.recent_progress) return [];
    
    // Group sessions by date and mode
    const grouped = {};
    stats.recent_progress.forEach((s) => {
      const date = new Date(s.created_at);
      const dayKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      if (!grouped[dayKey]) {
        grouped[dayKey] = { name: dayKey };
      }
      
      // Keep the best score for each mode per day
      if (!grouped[dayKey][s.mode] || s.score > grouped[dayKey][s.mode]) {
        grouped[dayKey][s.mode] = s.score;
      }
    });
    
    return Object.values(grouped);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Header user={user} />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <TrialBanner sub={sub} />

        {/* Welcome */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight mb-2">
            Welcome back, <span className="text-primary">{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="text-muted-foreground text-lg">Ready to train your memory?</p>
        </motion.div>

        {/* Quick Start */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card 
              className="bg-card border-border card-hover cursor-pointer h-full"
              onClick={() => navigate("/train/numbers")}
              data-testid="quick-start-numbers"
            >
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-lime-500/10 flex items-center justify-center mb-6">
                  <Hash className="w-8 h-8 text-lime-400" />
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-2">Number Training</h3>
                <p className="text-muted-foreground mb-4">
                  Memorize sequences of digits. 20 digits per line.
                </p>
                <div className="font-mono text-xl text-lime-400 tracking-widest">
                  48293750182
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card 
              className="bg-card border-border card-hover cursor-pointer h-full"
              onClick={() => navigate("/train/words")}
              data-testid="quick-start-words"
            >
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-secondary/10 flex items-center justify-center mb-6">
                  <Type className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-2">Word Training</h3>
                <p className="text-muted-foreground mb-4">
                  AI-generated word lists. Simple to complex.
                </p>
                <div className="text-lg text-secondary">
                  Algorithm • Philosophy
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card 
              className="bg-card border-border card-hover cursor-pointer h-full"
              onClick={() => navigate("/train/names")}
              data-testid="quick-start-names"
            >
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-orange-500/10 flex items-center justify-center mb-6">
                  <User className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-2">Names & Faces</h3>
                <p className="text-muted-foreground mb-4">
                  Remember names with faces. Perfect for networking.
                </p>
                <div className="text-lg text-orange-500">
                  Sarah • Michael • Emma
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card 
              className="bg-card border-border card-hover cursor-pointer h-full"
              onClick={() => navigate("/train/working-memory")}
              data-testid="quick-start-working-memory"
            >
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-purple-500/10 flex items-center justify-center mb-6">
                  <Grid3x3 className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-2">Working Memory</h3>
                <p className="text-muted-foreground mb-4">
                  Track items as the grid changes. Trains updating, not just holding.
                </p>
                <div className="text-lg text-purple-400">
                  Encode • Update • Probe
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Referral Card */}
        <ReferralCard sub={sub} />

        {/* Badges */}
        <AchievementsCard variant="strip" />

        {/* Streak Section */}
        {streak && (
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <Card className="bg-card border-border overflow-hidden">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  {/* Weekly Streak Counter */}
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 flex items-center justify-center rounded-full ${
                      streak.current_streak > 0 ? "bg-orange-500/20" : "bg-muted"
                    }`}>
                      <Flame className={`w-8 h-8 ${
                        streak.current_streak > 0 ? "text-orange-500" : "text-muted-foreground"
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-4xl font-bold ${
                          streak.current_streak > 0 ? "text-orange-500" : "text-muted-foreground"
                        }`}>
                          {streak.current_streak}
                        </span>
                        <span className="text-muted-foreground">week streak</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Best: {streak.longest_streak} weeks
                      </p>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="hidden md:block w-px h-16 bg-border" />

                  {/* This Week Progress */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">This Week</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {streak.current_week_days} / {streak.weekly_goal} days
                      </span>
                    </div>
                    <Progress 
                      value={(streak.current_week_days / streak.weekly_goal) * 100} 
                      className="h-3"
                    />
                    {streak.current_week_goal_met ? (
                      <p className="text-sm text-green-500 mt-2 flex items-center gap-1">
                        ✓ Weekly goal achieved!
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground mt-2">
                        {streak.weekly_goal - streak.current_week_days} more day{streak.weekly_goal - streak.current_week_days !== 1 ? 's' : ''} to hit your goal
                      </p>
                    )}
                  </div>

                  {/* Edit Goal Link */}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate("/profile")}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Edit Goal
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="bg-card border-border animate-pulse">
                <CardContent className="p-6">
                  <div className="h-8 bg-muted rounded mb-2"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : stats && (
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-card border-border" data-testid="stat-sessions">
              <CardContent className="p-6">
                <Target className="w-5 h-5 text-muted-foreground mb-2" />
                <div className="text-3xl font-bold text-primary">{stats.total_sessions}</div>
                <p className="text-sm text-muted-foreground">Total Sessions</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border" data-testid="stat-avg-score">
              <CardContent className="p-6">
                <TrendingUp className="w-5 h-5 text-muted-foreground mb-2" />
                <div className="text-3xl font-bold text-primary">{stats.avg_score}</div>
                <p className="text-sm text-muted-foreground">Avg Rating</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border" data-testid="stat-best-numbers">
              <CardContent className="p-6">
                <Hash className="w-5 h-5 text-lime-400 mb-2" />
                <div className="text-3xl font-bold text-lime-400">{stats.best_numbers_score}</div>
                <p className="text-sm text-muted-foreground">Best Numbers</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border" data-testid="stat-best-words">
              <CardContent className="p-6">
                <Type className="w-5 h-5 text-muted-foreground mb-2" />
                <div className="text-3xl font-bold text-secondary">{stats.best_words_score}</div>
                <p className="text-sm text-muted-foreground">Best Words</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border" data-testid="stat-best-accuracy">
              <CardContent className="p-6">
                <Target className="w-5 h-5 text-green-500 mb-2" />
                <div className="text-3xl font-bold text-green-500">{stats.best_accuracy || 0}</div>
                <p className="text-sm text-muted-foreground">Best Accuracy</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border" data-testid="stat-best-speed">
              <CardContent className="p-6">
                <Clock className="w-5 h-5 text-blue-500 mb-2" />
                <div className="text-3xl font-bold text-blue-500">{stats.best_speed || 0}</div>
                <p className="text-sm text-muted-foreground">Best Speed</p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Progress Chart */}
        {stats?.recent_progress?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Recent Progress</CardTitle>
                <Select value={timeframe} onValueChange={setTimeframe}>
                  <SelectTrigger className="w-32" data-testid="timeframe-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={formatChartData()}>
                      <XAxis 
                        dataKey="name" 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        domain={[0, 100]}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "0",
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="numbers" 
                        name="Numbers"
                        stroke="#d0f224"
                        strokeWidth={2}
                        dot={{ fill: "#d0f224", strokeWidth: 0 }}
                        connectNulls
                      />
                      <Line 
                        type="monotone" 
                        dataKey="words" 
                        name="Words"
                        stroke="#7000ff"
                        strokeWidth={2}
                        dot={{ fill: "#7000ff", strokeWidth: 0 }}
                        connectNulls
                      />
                      <Line 
                        type="monotone" 
                        dataKey="names" 
                        name="Names"
                        stroke="#f97316"
                        strokeWidth={2}
                        dot={{ fill: "#f97316", strokeWidth: 0 }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
